import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";
import type {
  TimeTrackEntry, TimeTrackNotice, TimeTrackTarget,
  startSchema, manualSchema, correctionSchema, transitionSchema, splitSchema,
} from "@shared/timetrack";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/contexts/auth-context";

export type TargetType = TimeTrackEntry["targetType"];
export interface TimeTrackOpenIntent {
  view?: "my" | "project" | "manual";
  projectId?: string;
  targetType?: TargetType;
  targetId?: string | number;
  workDate?: string;
}
export interface EntriesResponse { entries: TimeTrackEntry[]; serverNow: string; receivedAt: number }
export interface StateResponse {
  running: TimeTrackEntry | null;
  paused: TimeTrackEntry[];
  notices: TimeTrackNotice[];
  serverNow: string;
  receivedAt: number;
}
export interface TargetsResponse {
  targets: (TimeTrackTarget & { canTrack?: boolean; timeTrackingEnabled?: boolean })[];
  roster: { projectId: string; userId: number; name: string }[];
  serverNow: string;
  canTrack?: boolean;
  timeTrackingEnabled?: boolean;
}
export interface HistoryResponse {
  history: { id: string; actorId: number | null; actorName?: string | null; action: string; before: unknown; after: unknown; createdAt: string }[];
  segments: { id: string; startedAt: string; endedAt: string | null; durationSeconds: number; reviewedAt: string | null }[];
  serverNow: string;
}
type WithoutRequest<T> = Omit<T, "requestId">;
export type TimeTrackAction =
  | { action: "start"; data: WithoutRequest<z.input<typeof startSchema>> }
  | { action: "manual"; data: WithoutRequest<z.input<typeof manualSchema>> }
  | { action: "edit"; id: string; data: WithoutRequest<z.input<typeof correctionSchema>> }
  | { action: "split"; id: string; data: WithoutRequest<z.input<typeof splitSchema>> }
  | { action: "pause" | "resume" | "stop" | "snooze" | "review" | "restore" | "delete"; id: string; data: WithoutRequest<z.input<typeof transitionSchema>> };
export interface MutationResponse { entry?: TimeTrackEntry; entries?: TimeTrackEntry[]; notice?: string; serverNow: string }
const pendingRequestIds = new Map<string, string>();
export const TIMETRACK_EVENT = "open-timetrack";
export function openTimeTrack(intent: TimeTrackOpenIntent = { view: "my" }) {
  window.dispatchEvent(new CustomEvent<TimeTrackOpenIntent>(TIMETRACK_EVENT, { detail: intent }));
}
export function useTimeTrackAccess() {
  const { user, isAuthenticated, isLoading } = useAuth();
  return { user, enabled: !isLoading && isAuthenticated && !!user && (user.role === "admin" || user.role === "team") };
}
export function useTimeTrackOnline() {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => { window.removeEventListener("online", update); window.removeEventListener("offline", update); };
  }, []);
  return online;
}
async function getResource<T>(url: string): Promise<T & { receivedAt: number }> {
  const response = await apiRequest("GET", url);
  return { ...await response.json(), receivedAt: Date.now() };
}
export function useTimeTrackState(poll = true) {
  const { enabled, user } = useTimeTrackAccess();
  return useQuery<StateResponse>({
    queryKey: ["timetrack", user?.id, "state"],
    queryFn: () => getResource<StateResponse>("/api/timetrack/state"),
    enabled: enabled && poll, refetchInterval: poll ? 15000 : false, refetchOnWindowFocus: poll, staleTime: 5000,
  });
}
export function useTimeTrackTargets(load = true) {
  const { enabled, user } = useTimeTrackAccess();
  return useQuery<TargetsResponse>({
    queryKey: ["timetrack", user?.id, "targets"],
    queryFn: () => getResource<TargetsResponse>("/api/timetrack/targets"),
    enabled: enabled && load, staleTime: 30000, refetchOnWindowFocus: load,
  });
}
export function canTrackTimeTrackTarget(resources: TargetsResponse | undefined, projectId: string, targetType: TargetType, targetId: string | number) {
  if (!resources || resources.canTrack === false || resources.timeTrackingEnabled === false) return false;
  const project = resources.targets.find(target => target.projectId === projectId && target.targetType === "project" && target.targetId === projectId);
  const target = resources.targets.find(target => target.projectId === projectId && target.targetType === targetType && target.targetId === String(targetId));
  return !!target && project?.canTrack !== false && project?.timeTrackingEnabled !== false
    && target.canTrack !== false && target.timeTrackingEnabled !== false;
}
export function useTimeTrackEntries(filters: { projectId?: string; mine?: boolean; from: string; to: string; includeDeleted?: boolean }) {
  const { enabled, user } = useTimeTrackAccess();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value !== undefined && value !== "") params.set(key, String(value)); });
  return useQuery<EntriesResponse>({
    queryKey: ["timetrack", user?.id, "entries", filters],
    queryFn: () => getResource<EntriesResponse>(`/api/timetrack/entries?${params}`),
    enabled, refetchInterval: 15000, refetchOnWindowFocus: true, staleTime: 5000,
  });
}
export function useTimeTrackHistory(id: string, enabled: boolean) {
  const access = useTimeTrackAccess();
  return useQuery<HistoryResponse>({
    queryKey: ["timetrack", access.user?.id, "history", id],
    queryFn: () => getResource<HistoryResponse>(`/api/timetrack/entries/${encodeURIComponent(id)}/history`),
    enabled: access.enabled && enabled,
    staleTime: 0,
  });
}
export function useTimeTrackMutation() {
  const queryClient = useQueryClient();
  const { user } = useTimeTrackAccess();
  // Retain IDs across remounts and, in secure browsers, page reloads. Only a
  // SHA-256 fingerprint and request ID enter sessionStorage, never notes or names.
  return useMutation<MutationResponse, Error, TimeTrackAction, { previousRunning?: TimeTrackEntry | null }>({
    networkMode: "always",
    retry: false,
    onMutate: () => ({ previousRunning: queryClient.getQueryData<StateResponse>(["timetrack", user?.id, "state"])?.running }),
    mutationFn: async (intent) => {
      if (!navigator.onLine) throw new Error("You’re offline. Nothing was sent. Your server timer continues; reconnect before making changes.");
      const fingerprint = JSON.stringify([user?.id, intent]);
      let storageKey: string | undefined;
      let storedId: string | null = null;
      try {
        if (crypto.subtle) {
          const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(fingerprint));
          storageKey = `timetrack-request:${Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("")}`;
          storedId = sessionStorage.getItem(storageKey);
        }
      } catch { /* Memory retention still protects retries when storage is unavailable. */ }
      const requestId = pendingRequestIds.get(fingerprint) || storedId || crypto.randomUUID();
      pendingRequestIds.set(fingerprint, requestId);
      try { if (storageKey) sessionStorage.setItem(storageKey, requestId); } catch { /* Storage may be full. */ }
      const body = { ...intent.data, requestId };
      let method = "POST";
      let path: string;
      if (intent.action === "start") path = "/start";
      else if (intent.action === "manual") path = "/entries";
      else {
        path = `/entries/${encodeURIComponent(intent.id)}`;
        if (intent.action === "edit") method = "PATCH";
        else if (intent.action === "delete") method = "DELETE";
        else path += `/${intent.action}`;
      }
      try {
        if (!navigator.onLine) throw new Error("You’re offline. Nothing was sent. Reconnect before making changes.");
        const response = await apiRequest(method, `/api/timetrack${path}`, body);
        const data = await response.json();
        pendingRequestIds.delete(fingerprint);
        try { if (storageKey) sessionStorage.removeItem(storageKey); } catch { /* Memory has already been cleared. */ }
        return data;
      } catch (error) {
        if (error instanceof TypeError) throw new Error("The change is unconfirmed. Reconnect and refresh server state before retrying; your running timer has not been stopped locally.");
        throw error;
      }
    },
    onSuccess: (data, intent, context) => {
      const previous = context?.previousRunning;
      const switched = (intent.action === "start" || intent.action === "resume") && previous && data.entry && previous.id !== data.entry.id;
      const detail = data.notice || (switched
        ? `Paused “${previous.targetLabel}”. Now tracking “${data.entry!.targetLabel}”.`
        : undefined);
      if (detail) window.dispatchEvent(new CustomEvent("timetrack-notice", { detail }));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["timetrack", user?.id] });
    },
  });
}
export function useTimeTrackNoticeAcknowledge() {
  const queryClient = useQueryClient();
  const { user } = useTimeTrackAccess();
  return useMutation<{ acknowledged: boolean; serverNow: string }, Error, string>({
    networkMode: "always",
    retry: false,
    mutationFn: async id => {
      if (!navigator.onLine) throw new Error("Reconnect to acknowledge this notification.");
      const response = await apiRequest("POST", `/api/timetrack/notices/${encodeURIComponent(id)}/acknowledge`);
      return response.json();
    },
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ["timetrack", user?.id, "state"] }); },
  });
}
export function useTimeTrackNow() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}
